import React, {
  Fragment,
  useEffect,
  useRef,
  useCallback,
  useState,
} from "react";
import styled from "styled-components";
import { IEventGraphQL } from "@sellout/models/.dist/interfaces/IEvent";
import Masonry from "react-masonry-component";
import EventCard from "../components/EventCard";
import PageLoader from "../components/PageLoader";
import useNavigateToEventDetails from "../hooks/useNavigateToEventDetails.hook";
import useListEventsOptimizedHook from "../hooks/useListEventsOptimized.hook";
import NoPageContent, { NoPageContentTypes } from "../components/NoPageContent";
import { RolesEnum } from "@sellout/models/.dist/interfaces/IRole";
import usePermission from "../hooks/usePermission.hook";
import { AppNotificationTypeEnum } from "../models/interfaces/IAppNotification";
import * as AppActions from "../redux/actions/app.actions";
import { PaddedPage } from "../components/PageLayout";
import { Colors, Loader, LoaderSizes, Dropdown } from "@sellout/ui";
import { media } from "@sellout/ui/build/utils/MediaQuery";
import useNavigateToCreateEvent from "../hooks/useNavigateToCreateEvent.hook";
import { EventQueryEnum } from "../models/enums/EventQueryEnum";
import { useSelector, useDispatch } from "react-redux";
import { BackstageState } from "../redux/store";
import { useLocation } from "react-router";
import { LoaderContainer } from "../components/account/AccountStyle";
import PageSearch from "../components/PageSearch";
import { EventQueryOrderByEnum, EventQuerySortByEnum } from "@sellout/models/.dist/interfaces/IEventQuery";
import { useLazyQuery } from "@apollo/react-hooks";
import GET_EVENT from "@sellout/models/.dist/graphql/queries/event.query";
import * as EventActions from "../redux/actions/event.actions";

const SubNavContainer = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 5px;
  ${media.mobile`
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
  `};
`;

const TabsContainer = styled.div`
  display: flex;
`;

const SearchSortContainer = styled.div`
  display: flex;
  gap: 10px;
  align-items: stretch;
  ${media.mobile`
    flex-direction: column;
    gap: 15px;
  `};
`;

const SearchWrapper = styled.div`
  width: 375px;
  ${media.mobile`
    width: 100%;
  `};
`;

const SortWrapper = styled.div`
  width: 300px;
  ${media.mobile`
    width: 100%;
  `};
`;
const ScrollContainer = styled.div`
  overflow-y: auto;
  height: 90vh;
`;
type SubNavItemProps = {
  active: boolean;
};
const SubNavItem = styled.div<SubNavItemProps>`
  transition: all 0.2s;
  color: ${(props) => (props.active ? `${Colors.Grey1}` : `${Colors.Grey4}`)};
  font-weight: 600;
  font-size: 1.8rem;
  cursor: pointer;
  margin-right: 24px;
  padding-bottom: 14px;
   ${media.mobile`
      padding-bottom: 0px;

  `};
`;
export enum EventListTextEnum {
  Upcoming = "Upcoming",
  Past = "Past",
  Cancelled = "Cancelled",
  SearchPlaceholder = "Search by Name, Venue, Event Date (mm/dd/yyyy)",
  SortDateAsc = "Event Date (Ascending)",
  SortDateDesc = "Event Date (Descending)",
  ScannerNotification = "Web scanning coming soon. Please download the 'Sellout Access Control' app in the ios store or google play store from your phone in order to scan tickets."
}
type EventListProps = {
  match: any;
};

const EventList: React.FC<EventListProps> = () => {
  /* Hooks */
  const location = useLocation();
  const dispatch = useDispatch();

  const hasPermission = usePermission();
  const navigateToEventDetails = useNavigateToEventDetails();
  const navigateToCreateEvent = useNavigateToCreateEvent();
  const params: any = new URLSearchParams(location.search);

  const [queryKey, setQueryKey] = React.useState(
    EventQueryEnum.MainEventListUpComing
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [manualSortBy, setManualSortBy] = useState<EventQueryOrderByEnum | null>(null);
  
  const sortBy = manualSortBy ?? (
    queryKey === EventQueryEnum.MainEventListUpComing
      ? EventQueryOrderByEnum.Ascending
      : EventQueryOrderByEnum.Descending
  );

  const scrollContainer = useRef<HTMLDivElement>(null);
  const [hasMoreEvents, setHasMoreEvents] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const { eventQueryHash } = useSelector((state: BackstageState) => state.app);
  const baseQuery = eventQueryHash[queryKey];
  
  const parseDateSearch = (input: string) => {
    if (!input?.includes('/')) return null;
    const trimmed = input.trim();
    const currentYear = new Date().getUTCFullYear();
    
    const toEpoch = (year: number, month: number, day: number, isEndOfDay = false) => 
      Math.floor(Date.UTC(year, month, day, isEndOfDay ? 23 : 0, isEndOfDay ? 59 : 0, isEndOfDay ? 59 : 0, isEndOfDay ? 999 : 0) / 1000);
    
    //TODO Full date: mm/dd/yyyy
    if (/^(0?[1-9]|1[0-2])\/(0?[1-9]|[12][0-9]|3[01])\/\d{4}$/.test(trimmed)) {
      const [month, day, year] = trimmed.split('/').map(Number);
      return { searchStartDate: toEpoch(year, month - 1, day, false), searchEndDate: toEpoch(year, month - 1, day, true) };
    }
    
    //TODO Month/Day: mm/dd ( current year)
    if (/^(0?[1-9]|1[0-2])\/(0?[1-9]|[12][0-9]|3[01])$/.test(trimmed)) {
      const [month, day] = trimmed.split('/').map(Number);
      return { searchStartDate: toEpoch(currentYear, month - 1, day, false), searchEndDate: toEpoch(currentYear, month - 1, day, true) };
    }
    
    //TODO Month only: mm/
    if (/^(0?[1-9]|1[0-2])\/?$/.test(trimmed)) {
      const month = parseInt(trimmed.replace('/', ''));
      const lastDay = new Date(Date.UTC(currentYear, month, 0)).getUTCDate();
      return {
        searchStartDate: toEpoch(currentYear, month - 1, 1, false),
        searchEndDate: toEpoch(currentYear, month - 1, lastDay, true)
      };
    }
    
    return null;
  };

  const dateSearch = parseDateSearch(debouncedSearchQuery);
  const isDate = dateSearch !== null;

  const query = {
    ...baseQuery,
    name: !isDate && debouncedSearchQuery ? debouncedSearchQuery : undefined,
    sortBy: EventQuerySortByEnum.StartsAt,
    orderBy: sortBy,
    venueName: !isDate && debouncedSearchQuery ? debouncedSearchQuery : undefined,
    ...dateSearch,
  };

  const { events, loading, fetchMore } = useListEventsOptimizedHook({
    variables: {
      query,
      pagination: {
        pageSize: 20,
        pageNumber: 1,
      },
    },
  });

  const [getFullEvent] = useLazyQuery(GET_EVENT, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data?.event) {
        dispatch(EventActions.cacheEvents([data.event]));
      }
    },
  });

  const showScannerNotification = () =>
    dispatch(
      AppActions.showNotification(
        EventListTextEnum.ScannerNotification,
        AppNotificationTypeEnum.Warning
      )
    );
  const isScanner = hasPermission && !hasPermission(RolesEnum.BOX_OFFICE);
  const handleSortChange = (value: string) => {
    const orderBy = value === "date-desc" 
      ? EventQueryOrderByEnum.Descending 
      : EventQueryOrderByEnum.Ascending;
    setManualSortBy(orderBy);
  };
  useEffect(() => {
    if (EventQueryEnum[params.get("type")]) {
      setQueryKey(params.get("type"));
    }
  }, [params.get("type")]);
  useEffect(() => {
    if (
      scrollContainer.current &&
      events?.length &&
      hasMoreEvents &&
      typeof handleScroll === "function"
    ) {
      handleScroll(); 
    }
  }, [events?.length]);
  const handleScroll = useCallback(() => {
    if (
      !scrollContainer.current ||
      !fetchMore ||
      !hasMoreEvents ||
      isFetchingMore
    )
      return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainer.current;
    if (scrollTop + clientHeight >= scrollHeight - 500) {
      setIsFetchingMore(true);
      const requestedPageSize = 10;
      fetchMore({
        variables: {
          pagination: {
            pageSize: requestedPageSize,
            pageNumber: Math.ceil((events?.length || 0) / requestedPageSize) + 1,
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          setIsFetchingMore(false);

          if (
            !fetchMoreResult ||
            !fetchMoreResult?.events ||
            fetchMoreResult?.events?.length === 0
          ) {
            setHasMoreEvents(false);
            return prev || { events: [] };
          }

          const existingEventIds = new Set(
            (prev?.events ?? []).map((event: IEventGraphQL) => event._id)
          );
          const newEvents = fetchMoreResult.events.filter(
            (event) => !existingEventIds.has(event._id)
          );

          if (fetchMoreResult.events.length < requestedPageSize) {
            setHasMoreEvents(false);
          }

          return {
            ...prev,
            events: [...(prev?.events ?? []), ...newEvents],
          };
        },
      });
    }
  }, [events?.length, fetchMore, hasMoreEvents, isFetchingMore]);

  useEffect(() => {
    const container = scrollContainer.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [handleScroll, queryKey]);

  useEffect(() => {
    setHasMoreEvents(true);
    setIsFetchingMore(false);
    setManualSortBy(null);
    const shouldResetSearch = searchQuery !== "" || debouncedSearchQuery !== "";
    if (shouldResetSearch) {
      setSearchQuery("");
      setDebouncedSearchQuery("");
    }
  }, [queryKey]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    setHasMoreEvents(true);
    setIsFetchingMore(false);

    return () => clearTimeout(timer);
  }, [searchQuery, sortBy]);

  const sortOptions = [
    { value: "date-asc", text: EventListTextEnum.SortDateAsc },
    { value: "date-desc", text: EventListTextEnum.SortDateDesc },
  ];

  const currentSortValue = sortBy === EventQueryOrderByEnum.Descending ? "date-desc" : "date-asc";

  /* Render */
  return (
    <ScrollContainer ref={scrollContainer}>
      <Fragment>
        <PageLoader nav={true} fade={Boolean(!loading)} />
        <PaddedPage>
          <SubNavContainer>
            <TabsContainer>
              <SubNavItem
                onClick={() => setQueryKey(EventQueryEnum.MainEventListUpComing)}
                active={EventQueryEnum.MainEventListUpComing === queryKey}
              >
                {EventListTextEnum.Upcoming}
              </SubNavItem>
              <SubNavItem
                onClick={() => setQueryKey(EventQueryEnum.MainEventListPast)}
                active={EventQueryEnum.MainEventListPast === queryKey}
              >
                {EventListTextEnum.Past}
              </SubNavItem>
              <SubNavItem
                onClick={() => setQueryKey(EventQueryEnum.MainEventListCancelled)}
                active={EventQueryEnum.MainEventListCancelled === queryKey}
              >
                {EventListTextEnum.Cancelled}
              </SubNavItem>
            </TabsContainer>
            <SearchSortContainer>
              <SearchWrapper>
                <PageSearch
                  setSearchQuery={setSearchQuery}
                  searchQuery={searchQuery}
                  loading={loading}
                  placeHolder={EventListTextEnum.SearchPlaceholder}
                />
              </SearchWrapper>
              <SortWrapper>
                <Dropdown
                  value={sortOptions.find(opt => opt.value === currentSortValue)?.text || sortOptions[0].text}
                  items={sortOptions}
                  onChange={handleSortChange}
                  width="100%"
                  containerMinHeight="50px"
                  optionsHeight="46px"
                  itemContainerHeight="52px"
                  valueWhiteSpace="nowrap"
                />
              </SortWrapper>
            </SearchSortContainer>
          </SubNavContainer>

          {events && events.length > 0 && !loading ? (
            <Masonry
              options={{ horizontalOrder: true }}
              enableResizableChildren
            >
              {events.map((event: IEventGraphQL) => (
                <EventCard
                  key={event._id}
                  event={event}
                  margin="0 24px 24px 0"
                  onClick={async () => {
                    await getFullEvent({ variables: { eventId: event._id } });
                    if (isScanner) {
                      showScannerNotification();
                    } else if (event?.published) {
                      navigateToEventDetails(event._id);
                    } else {
                      navigateToCreateEvent(event._id);
                    }
                  }}
                />
              ))}
            </Masonry>
          ) : (
            !loading && <NoPageContent type={NoPageContentTypes.Event} />
          )}
          {isFetchingMore && (
            <LoaderContainer>
              <Loader size={LoaderSizes.Large} color={Colors.Orange} />
            </LoaderContainer>
          )}
        </PaddedPage>
      </Fragment>
    </ScrollContainer>
  );
};

export default EventList;
